<svelte:options
  customElement={{
    tag: "ofc-header-icon-v1",
    shadow: "none",
    props: {
      icon: { type: "String" },
      badgeType: { type: "String", attribute: "badge-type" },
      badgeIcon: { type: "String", attribute: "badge-icon" },
      badgeContent: { type: "String", attribute: "badge-content" },
      text: { type: "String" },
      ofcAriaLabel: { type: "String", attribute: "ofc-aria-label" },
      href: { type: "String" },
      encodedHref: { type: "String", attribute: "encoded-href" },
    },
  }}
/>

<script lang="ts">
  import type { Props } from "./HeaderIconV1.types";
  import { runWithRenderSlice } from "@otto-ec/global-resources/core";

  const {
    icon,
    badgeType,
    badgeIcon,
    badgeContent,
    text,
    ofcAriaLabel,
    href,
    encodedHref,
  }: Props = $props();

  let isTextOverflowingAbortController: AbortController;

  const overflowState = $state({
    textElement: undefined as HTMLElement | undefined,
    isOverflowing: false,
  });

  const computeOverflow = () => {
    isTextOverflowingAbortController?.abort();
    isTextOverflowingAbortController = new AbortController();

    runWithRenderSlice(
      () => {
        if (
          !overflowState.textElement ||
          overflowState.textElement.clientWidth < 1
        )
          return;

        const { scrollWidth, clientWidth } = overflowState.textElement;
        overflowState.isOverflowing = scrollWidth > clientWidth;
      },
      { signal: isTextOverflowingAbortController.signal, silent: true },
    );
  };
</script>

{#snippet content()}
  <div class="icon-container" aria-hidden="true">
    <oc-icon-v1 class="icon" type={icon} size={"100"}></oc-icon-v1>
    
    {#if badgeType && (badgeContent === undefined || badgeContent !== "0")}
      <oc-badge-v2 class="badge" variant={badgeType} icon={badgeIcon} size={"100"}
        >{badgeContent}</oc-badge-v2
      >
    {/if}
  </div>
  <div bind:this={overflowState.textElement} class="label" aria-hidden="true">{text}</div>
{/snippet}

{#snippet headerIconElement()}
  <oc-interactive-overlay-v1
    class="container"
    oc-aria-label={ofcAriaLabel}
    base64-href={encodedHref}
    onmouseover={computeOverflow}
    onfocus={computeOverflow}
    ontouchstart={computeOverflow}
  >
    <svelte:element this={href ? "a" : "div"} {href}>
      {@render content()}
    </svelte:element>
  </oc-interactive-overlay-v1>
{/snippet}

{#if overflowState.isOverflowing}
  <oc-tooltip-v1 class="tooltip" position="bottom" disabled={!overflowState.isOverflowing}>
    {@render headerIconElement()}
    <div slot="tooltip-content">{text}</div>
  </oc-tooltip-v1>
{:else}
  {@render headerIconElement()}
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "@otto-ec/otto-components-utils/scss/mixins";

  .container {
    --border-radius: var(--oc-semantic-border-radius-25);

    display: flex;
    flex-direction: column;
    text-align: center;
    font-size: 0;
    line-height: 1;
    width: 100%;
  }

  .icon-container {
    position: relative;
    display: inline-block;
  }

  .icon {
    color: tokens.$oc-semantic-color-text-secondary;
    vertical-align: middle;
  }

  .badge {
    position: absolute;
    bottom: 0;
    left: calc(100% - 0.5rem);
  }

  .label {
    font: tokens.$oc-semantic-font-copy-50 !important;
    color: tokens.$oc-semantic-color-text-secondary;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
    white-space: nowrap;

    @include mixins.breakpoint-from-l() {
      font: tokens.$oc-semantic-font-copy-75 !important;
    }
  }

  .tooltip {
    width: 100%;
    font-size: 0;
  }
</style>
